import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { Button } from '../ui/Button';
import { money } from '../money';

/** Dollars typed as whole cents, or null when it is not one (blank, below 0, finer than a cent). */
export function priceCentsOf(text: string): number | null {
  const x = Number(text) * 100;
  const n = Math.round(x);
  if (text.trim() === '' || !Number.isFinite(x) || Math.abs(n - x) > 1e-6 || n < 0)
    return null;
  return n;
}

/**
 * A part's price for this job (Inventory v2 12b, decision 5): set ahead of billing, it is what the
 * bill sheet starts from for anything not billed yet; a line already on an invoice keeps what it
 * printed. "Use list price" clears it. One save per tap (`useTapOnce`).
 */
export function JobPriceEditor({
  part,
  onDone,
}: {
  part: {
    jobCostId: string;
    item: { name: string };
    listPriceCents: number;
    jobPriceCents: number | null;
  };
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const [text, setText] = useState(
    part.jobPriceCents === null ? '' : (part.jobPriceCents / 100).toFixed(2),
  );
  const save = useMutation(
    trpc.partsBilling.setJobPrice.mutationOptions({
      onSettled: () => lock.release(),
      onSuccess: () => {
        void qc.invalidateQueries();
        onDone();
      },
    }),
  );
  const cents = priceCentsOf(text);
  const inputId = `job-price-${part.jobCostId}`;
  return (
    <div className="flex basis-full flex-wrap items-center gap-2 rounded-[var(--radius-md)] border border-border bg-surface-2 p-2 text-sm">
      <label htmlFor={inputId} className="text-xs font-semibold text-muted-foreground">
        Price for this job
      </label>
      <span className="inline-flex items-center gap-1">
        <span aria-hidden>$</span>
        <input
          id={inputId}
          autoFocus
          className="h-9 w-24 rounded-[var(--radius-sm)] border border-border bg-surface px-2 tabular-nums"
          inputMode="decimal"
          placeholder={(part.listPriceCents / 100).toFixed(2)}
          value={text}
          aria-invalid={text !== '' && cents === null ? true : undefined}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onDone();
          }}
        />
      </span>
      <span className="text-xs text-muted-foreground">
        List {money(part.listPriceCents)} each. Applies to anything not billed yet.
      </span>
      {save.error ? (
        <span className="basis-full text-xs text-[var(--sw-danger-text)]">
          {save.error.message}
        </span>
      ) : null}
      <span className="ml-auto flex gap-2">
        {part.jobPriceCents !== null ? (
          <Button
            variant="ghost"
            disabled={save.isPending}
            onClick={() =>
              sendOnce(lock, save.mutate, { jobCostId: part.jobCostId, priceCents: null })
            }
          >
            Use list price
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
        <Button
          disabled={cents === null || save.isPending}
          onClick={() =>
            cents !== null &&
            sendOnce(lock, save.mutate, {
              jobCostId: part.jobCostId,
              // The list price itself is no job price: it clears.
              priceCents: cents === part.listPriceCents ? null : cents,
            })
          }
        >
          {save.isPending ? 'Saving…' : 'Save price'}
        </Button>
      </span>
    </div>
  );
}
